import { RefObject, useEffect } from 'react';

import {
  CellPosition,
  CellValueChangedEvent,
  FirstDataRenderedEvent,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { FreshShipmentActions } from '@/app/slices/freshShipment';
import { WithLocalStorage } from '@/components';
import { useDefaultGridOptions } from '@/grid';
import { defaultFreshShipmentColDef } from '@/grid/defaults';
import { TOA_GetFreshShipmentFertList } from '@/rtk-query.gql';
import { useUpdateFertTransaction } from '@/services/fresh-shipment/hooks';
import { getLocalStorage, setLocalStorage } from '@/utils/storage';

import { csrViewGridColDefs } from '../utils/columnDefs';
import { gridOptions, rowClassRules } from '../utils/constants';
import { FreshShipmentFertList } from '../utils/types';

type FreshShipmentCsrGridProps = {
  className: string;
  themeMode: string;
  fertilizations: TOA_GetFreshShipmentFertList[] | undefined;
  me: string;
  quickSearchValue: string;
  handleUpdateFertilization: (params: CellValueChangedEvent) => void;
  onFirstDataRendered: (params: FirstDataRenderedEvent) => void;
  csrGridRef: RefObject<AgGridReact>;
};

export const FreshShipmentCsrGrid = ({
  className,
  themeMode,
  fertilizations,
  me,
  quickSearchValue,
  handleUpdateFertilization,
  onFirstDataRendered,
  csrGridRef,
}: FreshShipmentCsrGridProps) => {
  const defaultGridOptions = useDefaultGridOptions();
  const dispatch = useDispatch();
  const [params] = useSearchParams();
  const d5 = params.get('d5');

  const { mutateAsync: updateFertilization } = useUpdateFertTransaction();

  const selectedRows = getLocalStorage(
    'freshShipment',
    'selectedFreshShipmentRows',
  );

  useEffect(() => {
    if (csrGridRef.current?.api) {
      if (selectedRows.length > 0) {
        csrGridRef.current.api.forEachNode((node) => {
          if (selectedRows.includes(node.data.fertilizationId)) {
            node.setSelected(true);
          }
        });
      }
    }
  }, [quickSearchValue, selectedRows, csrGridRef]);

  const handleSelectionChanged = () => {
    const selectedRows = csrGridRef.current?.api.getSelectedRows();
    setLocalStorage(
      'freshShipment',
      (selectedRows as FreshShipmentFertList[]).map(
        (node) => node.fertilizationId,
      ),
      'selectedFreshShipmentRows',
    );
    dispatch({
      type: FreshShipmentActions.SET_SELECTED_ROWS,
      selectedRows: (selectedRows as FreshShipmentFertList[]).map(
        (node) => node.fertilizationId,
      ),
    });
  };

  const handleCellKeyDown = (event: any) => {
    if (event.event.key === 'Enter') {
      const currentCell: CellPosition = event.api.getFocusedCell();
      if (currentCell) {
        const nextRowIndex = currentCell.rowIndex + 1;
        event.api.setFocusedCell(nextRowIndex, currentCell.column);
        event.api.clearRangeSelection();
      }
    }
  };

  return (
    <div className="fresh-shipment-form__csr-grid">
      <WithLocalStorage storageKey={`fresh-shipments-csr-grid`}>
        <AgGridReact
          ref={csrGridRef}
          className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''}${className ? ` ${className}` : ''} with-vertical-lines`}
          rowData={fertilizations}
          getRowId={(params) => params.data.fertilizationId}
          defaultColDef={defaultFreshShipmentColDef}
          columnDefs={csrViewGridColDefs}
          overlayLoadingTemplate="Please Select a Site and a Date"
          rowSelection="multiple"
          rowClassRules={rowClassRules}
          onCellValueChanged={handleUpdateFertilization}
          onSelectionChanged={handleSelectionChanged}
          onCellKeyDown={handleCellKeyDown}
          context={{
            me,
            d5,
            handleUpdateFertilization: (params: CellValueChangedEvent) => {
              updateFertilization({
                id: params.data.fertilizationId,
                field: params.column.getColDef().field as string,
                value:
                  params.newValue !== undefined
                    ? params.newValue
                    : params.data[params.column.getColDef().field as string],
                updatePredictedComments: false,
              });
            },
          }}
          quickFilterText={quickSearchValue}
          gridOptions={{ ...defaultGridOptions, ...gridOptions }}
          suppressRowClickSelection={true}
          suppressRowHoverHighlight={true}
          onFirstDataRendered={onFirstDataRendered}
          getRowHeight={() => 30}
        />
      </WithLocalStorage>
    </div>
  );
};
